import { showConfirm } from "~/components/ui/alert";
import { supabase } from "~/integrations/supabase/client";
import { Address } from "~/types/client";

/**
 * Fetches all addresses for a client
 */
export const fetchClientAddresses = async (
  clientId: string
): Promise<Address[]> => {
  if (!clientId) return [];

  try {
    const { data, error } = await supabase
      .from("addresses")
      .select("*")
      .eq("client_id", clientId)
      .order("is_default", { ascending: false });

    if (error) throw error;

    const formattedAddresses: Address[] = data.map((item) => ({
      id: item.id,
      clientId: item.client_id,
      street: item.street,
      number: item.number,
      complement: item.complement || undefined,
      city: item.city,
      state: item.state || undefined,
      comuna: item.comuna || undefined,
      isDefault: item.is_default || false,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
    }));

    return formattedAddresses;
  } catch (error) {
    console.error("Error fetching addresses:", error);
    throw error;
  }
};

/**
 * Adds a new address for a client
 */
export const addClientAddress = async (
  clientId: string,
  values: {
    street: string;
    number: string;
    complement?: string;
    city: string;
    state?: string;
    comuna?: string;
    isDefault: boolean;
  },
  regionName?: string,
  comunaName?: string
): Promise<boolean> => {
  if (!clientId) return false;

  try {
    if (values.isDefault) {
      await supabase
        .from("addresses")
        .update({ is_default: false })
        .eq("client_id", clientId);
    }

    const { error } = await supabase.from("addresses").insert({
      client_id: clientId,
      street: values.street,
      number: values.number,
      complement: values.complement || null,
      city: values.city || "Santiago", // Prevent empty city values
      state: regionName || values.state || null,
      comuna: comunaName || null,
      is_default: values.isDefault,
    });

    if (error) throw error;

    return true;
  } catch (error) {
    console.error("Error adding address:", error);
    throw error;
  }
};

/**
 * Updates an existing address and syncs denormalized sales request fields
 */
export const updateClientAddress = async (
  clientId: string,
  addressId: string,
  values: {
    street: string;
    number: string;
    complement?: string;
    city: string;
    state?: string;
    comuna?: string;
    postalCode?: string;
    isDefault: boolean;
  },
  regionName?: string,
  comunaName?: string
): Promise<boolean> => {
  if (!clientId) return false;

  try {
    if (values.isDefault) {
      await supabase
        .from("addresses")
        .update({ is_default: false })
        .eq("client_id", clientId);
    }

    const { error } = await supabase
      .from("addresses")
      .update({
        street: values.street,
        number: values.number,
        complement: values.complement || null,
        city: values.city || "Santiago", // Prevent empty city values
        state: regionName || values.state || null,
        comuna: comunaName || null,
        postal_code: values.postalCode || null,
        is_default: values.isDefault,
      })
      .eq("id", addressId);

    if (error) throw error;

    // Sync denormalized delivery fields in sales_request table
    // Update all sales requests that use this delivery address
    const syncError = await syncSalesRequestDeliveryFields(addressId, {
      deliveryStreet: values.street,
      deliveryNumber: values.number,
      deliveryComplement: values.complement || null,
      deliveryCity: values.city || "Santiago",
      deliveryState: regionName || values.state || null,
      deliveryComuna: comunaName || null,
    });

    if (syncError) {
      console.error(
        "Warning: Could not sync sales request delivery fields:",
        syncError
      );
      // Don't fail the address update if sync fails - log as warning
    }

    return true;
  } catch (error) {
    console.error("Error updating address:", error);
    throw error;
  }
};

/**
 * Deletes an address after confirmation
 */
export const deleteClientAddress = async (
  addressId: string
): Promise<boolean> => {
  const result = await showConfirm({
    title: "¿Eliminar dirección?",
    text: "Esta acción no se puede deshacer",
    icon: "warning",
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
  });

  if (!result.isConfirmed) return false;

  try {
    const { error } = await supabase
      .from("addresses")
      .delete()
      .eq("id", addressId);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error("Error deleting address:", error);
    throw error;
  }
};

/**
 * Fetches the fiscal address for a client
 */
export const fetchClientFiscalAddress = async (
  clientId: string
): Promise<Address | null> => {
  if (!clientId) return null;

  try {
    // First get the fiscal address ID from the client
    const { data: clientData, error: clientError } = await supabase
      .from("clients")
      .select("fiscal_address_id")
      .eq("id", clientId)
      .single();

    if (clientError || !clientData.fiscal_address_id) return null;

    // Then fetch the actual address
    const { data, error } = await supabase
      .from("addresses")
      .select("*")
      .eq("id", clientData.fiscal_address_id)
      .single();

    if (error) throw error;

    return {
      id: data.id,
      clientId: data.client_id,
      street: data.street,
      number: data.number,
      complement: data.complement || undefined,
      city: data.city,
      state: data.state || undefined,
      comuna: data.comuna || undefined,
      isDefault: data.is_default || false,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  } catch (error) {
    console.error("Error fetching fiscal address:", error);
    return null;
  }
};

/**
 * Sets an existing address as the client's fiscal address
 */
export const setClientFiscalAddress = async (
  clientId: string,
  addressId: string
): Promise<boolean> => {
  if (!clientId || !addressId) return false;

  try {
    const { error } = await supabase
      .from("clients")
      .update({ fiscal_address_id: addressId })
      .eq("id", clientId);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error("Error setting fiscal address:", error);
    throw error;
  }
};

/**
 * Syncs denormalized delivery fields in sales_request table when an address is updated
 */
const syncSalesRequestDeliveryFields = async (
  addressId: string,
  deliveryFields: {
    deliveryStreet: string;
    deliveryNumber: string;
    deliveryComplement: string | null;
    deliveryCity: string;
    deliveryState: string | null;
    deliveryComuna: string | null;
  }
): Promise<Error | null> => {
  try {
    const { error } = await supabase
      .from("sales_request")
      .update({
        delivery_street: deliveryFields.deliveryStreet,
        delivery_number: deliveryFields.deliveryNumber,
        delivery_complement: deliveryFields.deliveryComplement,
        delivery_city: deliveryFields.deliveryCity,
        delivery_state: deliveryFields.deliveryState,
        delivery_comuna: deliveryFields.deliveryComuna,
        updated_at: new Date().toISOString(),
      })
      .eq("delivery_address_id", addressId);

    return error;
  } catch (error) {
    return error as Error;
  }
};

/**
 * Adds a new address and sets it as the fiscal address
 */
export const addAndSetFiscalAddress = async (
  clientId: string,
  values: {
    street: string;
    number: string;
    complement?: string;
    city: string;
    state?: string;
    comuna?: string;
    isDefault: boolean;
  },
  regionName?: string,
  comunaName?: string
): Promise<boolean> => {
  if (!clientId) return false;

  try {
    // Insert the new address
    const { data, error } = await supabase
      .from("addresses")
      .insert({
        client_id: clientId,
        street: values.street,
        number: values.number,
        complement: values.complement || null,
        city: values.city || "Santiago", // Prevent empty city values
        state: regionName || values.state || null,
        comuna: comunaName || null,
        is_default: values.isDefault,
      })
      .select("id")
      .single();

    if (error) throw error;

    // Set this address as the fiscal address
    const addressId = data.id;
    return await setClientFiscalAddress(clientId, addressId);
  } catch (error) {
    console.error("Error adding and setting fiscal address:", error);
    throw error;
  }
};
